/**
 * Settings Dashboard (User V2)
 * =============================
 * 
 * User account settings and company address management.
 * Users can view and add addresses for their company.
 */

import React, { useState, useEffect } from 'react';
import UserLayout from '../../layout/UserLayout';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import Toast from '../../../Toast';
import { DeleteButtonWithConfirm, UpdateButton } from '../../../common/buttons';
import './SettingsDashboard.scss';

interface SettingsDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const SettingsDashboard: React.FC<SettingsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token, user: authUser } = useAuth();

  // State
  const [addresses, setAddresses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Turkey'
  });
  const [editingAddress, setEditingAddress] = useState<string | null>(null);
  const [editAddressForm, setEditAddressForm] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Turkey'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Approval Settings State
  const [approvalSettings, setApprovalSettings] = useState({
    enabled: true,
    duration: '1 year'
  });
  const [workflowSettings, setWorkflowSettings] = useState({
    require_approval: true,
    require_control: true
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  // Load addresses
  const loadAddresses = async () => {
    const companyId = user.company_id || user.company?._id || user.company;

    console.log('Loading addresses for company:', companyId);

    if (!token || !companyId) {
      console.warn('Cannot load addresses - missing token or company_id');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(
        API_ENDPOINTS.COMPANY_ADDRESSES(companyId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAddresses(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load company settings
  const loadCompanySettings = async () => {
    const companyId = user.company_id || user.company?._id || user.company;
    if (!token || !companyId) return;

    try {
      // Load approval settings
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          if (data.data.approval_settings) {
            setApprovalSettings({
              enabled: data.data.approval_settings.enabled ?? true,
              duration: data.data.approval_settings.duration || '1 year'
            });
          }
          // Load workflow settings
          if (data.data.rfq_workflow_settings) {
            setWorkflowSettings(data.data.rfq_workflow_settings);
          }
        }
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  // Load addresses on mount


  // Load addresses on mount
  useEffect(() => {
    loadAddresses();
    loadCompanySettings();
    fetchCartCount();
  }, [token, user.company_id, user.company, authUser?._id]);

  const fetchCartCount = async () => {
    try {
      const localToken = localStorage.getItem('auth_token');
      const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localToken}`,
          'X-User-ID': authUser?._id || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCartItemCount(data.data?.items?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching cart count:', error);
    }
  };

  // Handle add address
  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newAddress.street || !newAddress.city || !newAddress.postal_code || !newAddress.country) {
      setToast({
        message: 'Please fill in all required fields',
        type: 'error',
        isVisible: true
      });
      return;
    }

    const companyId = user.company_id || user.company?._id || user.company;

    if (!companyId) {
      setToast({
        message: 'Company ID not found. Please contact support.',
        type: 'error',
        isVisible: true
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        API_ENDPOINTS.COMPANY_ADDRESSES(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newAddress)
        }
      );

      if (response.ok) {
        setToast({
          message: 'Address added successfully!',
          type: 'success',
          isVisible: true
        });
        setNewAddress({
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'Turkey'
        });
        setShowAddForm(false);
        await loadAddresses();
      } else {
        const errorData = await response.json();
        setToast({
          message: errorData.message || 'Failed to add address',
          type: 'error',
          isVisible: true
        });
      }
    } catch (error) {
      console.error('Error adding address:', error);
      setToast({
        message: 'Failed to add address. Please try again.',
        type: 'error',
        isVisible: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete address
  const handleDeleteAddress = async (addressId: string) => {
    const companyId = user.company_id || user.company?._id || user.company;

    if (!companyId) {
      setToast({
        message: 'Company ID not found. Please contact support.',
        type: 'error',
        isVisible: true
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_ENDPOINTS.COMPANY_ADDRESSES(companyId)}/${addressId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setToast({
          message: 'Address deleted successfully!',
          type: 'success',
          isVisible: true
        });
        await loadAddresses();
      } else {
        const errorData = await response.json();
        setToast({
          message: errorData.message || 'Failed to delete address',
          type: 'error',
          isVisible: true
        });
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      setToast({
        message: 'Failed to delete address. Please try again.',
        type: 'error',
        isVisible: true
      });
    }
  };

  // Handle set default address
  const handleSetDefault = async (addressId: string) => {
    const companyId = user.company_id || user.company?._id || user.company;

    if (!companyId) {
      alert('Company ID not found. Please contact support.');
      return;
    }

    try {
      const response = await fetch(
        `${API_ENDPOINTS.COMPANY_ADDRESSES(companyId)}/${addressId}/set-default`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setToast({
          message: 'Default address updated!',
          type: 'success',
          isVisible: true
        });
        await loadAddresses();
      } else {
        const errorData = await response.json();
        setToast({
          message: errorData.message || 'Failed to set default address',
          type: 'error',
          isVisible: true
        });
      }
    } catch (error) {
      console.error('Error setting default:', error);
      setToast({
        message: 'Failed to set default address. Please try again.',
        type: 'error',
        isVisible: true
      });
    }
  };

  // Handle edit address
  const handleEditAddress = (address: any) => {
    setEditingAddress(address._id);
    setEditAddressForm({
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || 'Turkey'
    });
  };

  // Handle update address
  const handleUpdateAddress = async (e: React.FormEvent, addressId: string) => {
    e.preventDefault();

    if (!editAddressForm.street || !editAddressForm.city || !editAddressForm.postal_code || !editAddressForm.country) {
      setToast({
        message: 'Please fill in all required fields',
        type: 'error',
        isVisible: true
      });
      return;
    }

    const companyId = user.company_id || user.company?._id || user.company;

    if (!companyId) {
      setToast({
        message: 'Company ID not found. Please contact support.',
        type: 'error',
        isVisible: true
      });
      return;
    }

    setIsUpdating(true);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.COMPANY_ADDRESS(companyId, addressId)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editAddressForm)
        }
      );

      if (response.ok) {
        setToast({
          message: 'Address updated successfully!',
          type: 'success',
          isVisible: true
        });
        setEditingAddress(null);
        setEditAddressForm({
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'Turkey'
        });
        await loadAddresses();
      } else {
        const errorData = await response.json();
        setToast({
          message: errorData.message || 'Failed to update address',
          type: 'error',
          isVisible: true
        });
      }
    } catch (error) {
      console.error('Error updating address:', error);
      setToast({
        message: 'Failed to update address. Please try again.',
        type: 'error',
        isVisible: true
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingAddress(null);
    setEditAddressForm({
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Turkey'
    });
  };

  // Handle save approval settings
  const handleSaveApprovalSettings = async () => {
    const companyId = user.company_id || user.company?._id || user.company;
    if (!companyId) return;

    setIsSavingSettings(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            approval_settings: approvalSettings
          })
        }
      );

      if (response.ok) {
        setToast({
          message: 'Settings saved successfully!',
          type: 'success',
          isVisible: true
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({
        message: 'Failed to save settings',
        type: 'error',
        isVisible: true
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Handle save workflow settings
  const handleSaveWorkflowSettings = async () => {
    const companyId = user.company_id || user.company?._id || user.company;
    if (!companyId) return;

    setIsSavingWorkflow(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/rfq-workflow`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(workflowSettings)
        }
      );

      if (response.ok) {
        setToast({
          message: 'Workflow settings saved successfully!',
          type: 'success',
          isVisible: true
        });
      } else {
        throw new Error('Failed to save workflow settings');
      }
    } catch (error) {
      console.error('Error saving workflow settings:', error);
      setToast({
        message: 'Failed to save workflow settings',
        type: 'error',
        isVisible: true
      });
    } finally {
      setIsSavingWorkflow(false);
    }
  };

  // Get user role display info
  const getUserRoleInfo = () => {
    // User's role field contains the role ID (ObjectId or string)
    // We need to find this role ID in the company's roles array to get the role name
    const userRoleId = authUser?.role || user?.role;
    // Get company from authUser first (most up-to-date), then fallback to user prop
    const company = (authUser as any)?.company || user?.company;
    const permissions = user?.permissions || authUser?.permissions || {};
    const isAdmin = user?.is_admin === true || authUser?.is_admin === true;

    let roleLabel = 'User';
    let roleDescription = 'Standard user with basic access';
    let permissionsList: string[] = [];

    // Check if user is admin (system admin)
    if (isAdmin || userRoleId === 'admin') {
      roleLabel = 'System Administrator';
      roleDescription = 'Full system access with all permissions';
      permissionsList = ['All permissions'];
      return { roleLabel, roleDescription, permissionsList };
    }

    // Try to find role name from company roles array
    if (company && userRoleId && typeof company === 'object' && company.roles) {
      const roles = Array.isArray(company.roles) ? company.roles : [];

      // Convert userRoleId to string for comparison
      const userRoleIdStr = String(userRoleId);

      // Find matching role in company roles
      const matchedRole = roles.find((role: any) => {
        const roleId = role._id || role.role_id;
        return String(roleId) === userRoleIdStr || String(role._id) === userRoleIdStr;
      });

      if (matchedRole && matchedRole.name) {
        roleLabel = matchedRole.name;

        // Get permissions from matched role's permissions object (most accurate)
        if (matchedRole.permissions && typeof matchedRole.permissions === 'object') {
          // Permission name mapping for display
          const permissionLabels: Record<string, string> = {
            'open_rfq_request': 'Open RFQ Request',
            'edit_supplier': 'Edit Supplier',
            'edit_product': 'Edit Product',
            'approve_ability': 'Approve Ability',
            'see_all_requests': 'See All Requests',
            'user_rights': 'User Rights',
            'edit_address': 'Edit Address'
          };

          // Extract true permissions from role's permissions object
          permissionsList = Object.entries(matchedRole.permissions)
            .filter(([key, value]) => value === true)
            .map(([key]) => {
              // Use mapping if available, otherwise format the key
              return permissionLabels[key] || key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            });
        }

        // Set description based on role name
        const roleNameLower = roleLabel.toLowerCase();
        if (roleNameLower.includes('admin') || roleNameLower.includes('administrator')) {
          roleDescription = 'Company-level administration with team management';
          if (permissionsList.length === 0) {
            permissionsList = ['Company Management', 'Team Management'];
          }
        } else {
          roleDescription = 'Standard user with role-based permissions';
        }
      }
    }

    // Fallback: Try role_name from backend if available
    if (!roleLabel || roleLabel === 'User') {
      const roleName = (authUser as any)?.role_name || (user as any)?.role_name;
      if (roleName) {
        roleLabel = roleName;
      }
    }

    // If still no permissions found, try from user permissions object
    if (permissionsList.length === 0 && typeof permissions === 'object' && !Array.isArray(permissions)) {
      const permissionLabels: Record<string, string> = {
        'open_rfq_request': 'Open RFQ Request',
        'edit_supplier': 'Edit Supplier',
        'edit_product': 'Edit Product',
        'approve_ability': 'Approve Ability',
        'see_all_requests': 'See All Requests',
        'user_rights': 'User Rights',
        'edit_address': 'Edit Address'
      };

      permissionsList = Object.entries(permissions)
        .filter(([key, value]) => value === true)
        .map(([key]) => {
          return permissionLabels[key] || key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        });
    }

    return { roleLabel, roleDescription, permissionsList };
  };

  const { roleLabel, roleDescription, permissionsList } = getUserRoleInfo();

  // Check if user has approve_ability permission (for address management)
  const hasAddressPermission = user?.permissions?.approve_ability || authUser?.permissions?.approve_ability || false;

  return (
    <UserLayout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle="Settings"
      pageDescription="Manage your account and company addresses"
      cartItemCount={cartItemCount}
      disableInnerScroll={true}
    >
      <div className="settings-dashboard">
        {/* User Information Section */}
        <div className="settings-section">
          <div className="section-header">
            <h2>User Information</h2>
          </div>

          <div className="user-info-card">
            <div className="user-info-row">
              <div className="info-label">Name</div>
              <div className="info-value">{user?.name || 'N/A'}</div>
            </div>
            <div className="user-info-row">
              <div className="info-label">Email</div>
              <div className="info-value">{user?.email || 'N/A'}</div>
            </div>
            <div className="user-info-row">
              <div className="info-label">Company</div>
              <div className="info-value">{user?.company?.name || user?.company || 'N/A'}</div>
            </div>
            <div className="user-info-row">
              <div className="info-label">Role</div>
              <div className="info-value">
                <span className="role-badge">{roleLabel}</span>
              </div>
            </div>
            <div className="user-info-row">
              <div className="info-label">Role Description</div>
              <div className="info-value">{roleDescription}</div>
            </div>
            {permissionsList.length > 0 && (
              <div className="user-info-row permissions-row">
                <div className="info-label">Permissions</div>
                <div className="info-value permissions-list">
                  {permissionsList.map((perm, index) => (
                    <span key={index} className="permission-badge">
                      {perm}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>


        {/* Approval Validation Date Section */}
        {hasAddressPermission && ( // Using same permission check for now (admin/key user)
          <div className="settings-section">
            <div className="section-header">
              <h2>Approval Validation Date</h2>
              <button
                className="btn-primary"
                onClick={handleSaveApprovalSettings}
                disabled={isSavingSettings}
              >
                {isSavingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            <div className="settings-card">
              <div className="form-row">
                <div className="approval-toggle-container">
                  <div className="approval-toggle-row">
                    <span className="toggle-label-text">Enable Approval Validation Date</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={approvalSettings.enabled}
                        onChange={(e) => setApprovalSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <p className="help-text" style={{ marginTop: '-15px', marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
                    When enabled, approved suppliers will have a validity period starting from the document upload date.
                  </p>
                </div>
              </div>

              {approvalSettings.enabled && (
                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: '300px' }}>
                    <label>Validity Duration</label>
                    <select
                      value={approvalSettings.duration}
                      onChange={(e) => setApprovalSettings(prev => ({ ...prev, duration: e.target.value }))}
                      className="form-select"
                    >
                      <option value="3 months">3 Months</option>
                      <option value="6 months">6 Months</option>
                      <option value="1 year">1 Year</option>
                      <option value="2 years">2 Years</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RFQ Workflow Settings Section */}
        {hasAddressPermission && (
          <div className="settings-section">
            <div className="section-header">
              <h2>RFQ Workflow Configuration</h2>
              <button
                className="btn-primary"
                onClick={handleSaveWorkflowSettings}
                disabled={isSavingWorkflow}
              >
                {isSavingWorkflow ? 'Saving...' : 'Save Workflow Settings'}
              </button>
            </div>

            <div className="settings-card">
              <p className="help-text" style={{ marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
                Configure which approval steps are required for RFQs in your company.
              </p>

              <div className="form-row">
                <div className="approval-toggle-container">
                  <div className="approval-toggle-row">
                    <span className="toggle-label-text">Require Approval Step</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={workflowSettings.require_approval}
                        onChange={(e) => setWorkflowSettings(prev => ({ ...prev, require_approval: e.target.checked }))}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <p className="help-text" style={{ marginTop: '-15px', marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
                    RFQs must be approved before proceeding to document preparation.
                  </p>
                </div>
              </div>

              <div className="form-row">
                <div className="approval-toggle-container">
                  <div className="approval-toggle-row">
                    <span className="toggle-label-text">Require Control Step</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={workflowSettings.require_control}
                        onChange={(e) => setWorkflowSettings(prev => ({ ...prev, require_control: e.target.checked }))}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <p className="help-text" style={{ marginTop: '-15px', marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
                    RFQs require control verification after approval.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Address Management Section */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Company Addresses</h2>
            {hasAddressPermission && !showAddForm && (
              <button
                className="btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                + Add New Address
              </button>
            )}
            {!hasAddressPermission && (
              <span style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                Read-only mode
              </span>
            )}
          </div>

          {/* Inline Add Address Form */}
          {hasAddressPermission && showAddForm && (
            <div className="add-address-form">
              <h3>Add New Address</h3>
              <form onSubmit={handleAddAddress}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="street">Street Address *</label>
                    <input
                      type="text"
                      id="street"
                      value={newAddress.street}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                      placeholder="e.g., 123 Business Street, Floor 5"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      value={newAddress.city}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="e.g., Istanbul"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State/Province</label>
                    <input
                      type="text"
                      id="state"
                      value={newAddress.state}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="postal_code">Postal Code *</label>
                    <input
                      type="text"
                      id="postal_code"
                      value={newAddress.postal_code}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                      placeholder="e.g., 34000"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country *</label>
                    <input
                      type="text"
                      id="country"
                      value={newAddress.country}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                      placeholder="e.g., Turkey"
                      required
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewAddress({
                        street: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'Turkey'
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting || !newAddress.street || !newAddress.city || !newAddress.postal_code || !newAddress.country}
                  >
                    {isSubmitting ? 'Adding...' : 'Add Address'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {isLoading ? (
            <div className="loading">Loading addresses...</div>
          ) : addresses.length === 0 && !showAddForm ? (
            <div className="empty-state">
              <p>No addresses found. Add your first address to get started.</p>
            </div>
          ) : (
            <div className="address-list">
              {addresses.map((address) => (
                <div key={address._id}>
                  {editingAddress === address._id ? (
                    <div className="edit-address-form">
                      <h3>Edit Address</h3>
                      <form onSubmit={(e) => handleUpdateAddress(e, address._id)}>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`edit-street-${address._id}`}>Street Address *</label>
                            <input
                              type="text"
                              id={`edit-street-${address._id}`}
                              value={editAddressForm.street}
                              onChange={(e) => setEditAddressForm(prev => ({ ...prev, street: e.target.value }))}
                              placeholder="e.g., 123 Business Street, Floor 5"
                              required
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`edit-city-${address._id}`}>City *</label>
                            <input
                              type="text"
                              id={`edit-city-${address._id}`}
                              value={editAddressForm.city}
                              onChange={(e) => setEditAddressForm(prev => ({ ...prev, city: e.target.value }))}
                              placeholder="e.g., Istanbul"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor={`edit-state-${address._id}`}>State/Province</label>
                            <input
                              type="text"
                              id={`edit-state-${address._id}`}
                              value={editAddressForm.state}
                              onChange={(e) => setEditAddressForm(prev => ({ ...prev, state: e.target.value }))}
                              placeholder="Optional"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`edit-postal_code-${address._id}`}>Postal Code *</label>
                            <input
                              type="text"
                              id={`edit-postal_code-${address._id}`}
                              value={editAddressForm.postal_code}
                              onChange={(e) => setEditAddressForm(prev => ({ ...prev, postal_code: e.target.value }))}
                              placeholder="e.g., 34000"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor={`edit-country-${address._id}`}>Country *</label>
                            <input
                              type="text"
                              id={`edit-country-${address._id}`}
                              value={editAddressForm.country}
                              onChange={(e) => setEditAddressForm(prev => ({ ...prev, country: e.target.value }))}
                              placeholder="e.g., Turkey"
                              required
                            />
                          </div>
                        </div>

                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                          >
                            Cancel
                          </button>
                          <UpdateButton
                            onUpdate={(e) => handleUpdateAddress(e, address._id)}
                            disabled={!editAddressForm.street || !editAddressForm.city || !editAddressForm.postal_code || !editAddressForm.country}
                            isLoading={isUpdating}
                            label="Update Address"
                          />
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className={`address-card ${address.is_default ? 'default' : ''}`}>
                      {address.is_default && <span className="default-badge">Default</span>}
                      <div className="address-content">
                        <div className="address-header">
                          <h3>{address.street}</h3>
                        </div>
                        <p className="address-text">
                          {address.city}
                          {address.state && `, ${address.state}`}
                          {' '}{address.postal_code}
                          {', '}{address.country}
                        </p>
                        <div className="address-meta">
                          <span>Added: {new Date(address.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {hasAddressPermission && (
                        <div className="address-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEditAddress(address)}
                          >
                            Edit
                          </button>
                          {!address.is_default && (
                            <button
                              className="btn-set-default"
                              onClick={() => handleSetDefault(address._id)}
                            >
                              Set as Default
                            </button>
                          )}
                          <DeleteButtonWithConfirm
                            onDelete={() => handleDeleteAddress(address._id)}
                            label="Delete"
                            confirmTitle="Delete Address"
                            confirmMessage="Are you sure you want to delete this address? This action cannot be undone."
                            confirmText="Delete"
                            cancelText="Cancel"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        duration={4000}
      />
    </UserLayout >
  );
};

export default SettingsDashboard;
